import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ImageUpload } from '@/components/ImageUpload';
import { PhotoSourceTabs } from '@/components/PhotoSourceTabs';
import { PhotoLibraryPicker } from '@/components/PhotoLibraryPicker';
import { usePhotoLibrary } from '@/hooks/usePhotoLibrary';
import { findBestLibraryMatch } from '@/lib/photoLibraryMatch';
import { isLibraryUrl } from '@/lib/photoLibraryTypes';
import { POWER_SOURCES, CIRCULATION_MODES } from '@/lib/constants';
import { isToolCategory, ITEM_CATEGORY_OPTIONS, type ItemCategory } from '@/lib/itemCategoryConfig';
import {
  createEmptyCategoryDetailsState,
  saveCategoryDetails,
  validateCategoryDetails,
  type CategoryDetailsState,
} from '@/lib/itemCategoryDetails';
import { useCreateItem } from '@/hooks/useItems';
import type { CirculationMode, PowerSource } from '@/hooks/useItems';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface QuickAddItemFormProps {
  groupId: string;
  onItemCreated?: (item: { id: string; name: string; category: string }) => void;
  resetOnSubmit?: boolean;
  submitLabel?: string;
  /** When true, shows only Name + Category — hides photo, circulation rules, and additional details to reduce onboarding cognitive load */
  isOnboarding?: boolean;
}

/**
 *
 * @param root0
 * @param root0.groupId
 * @param root0.onItemCreated
 * @param root0.resetOnSubmit
 * @param root0.submitLabel
 * @param root0.isOnboarding
 */
export function QuickAddItemForm({
  groupId,
  onItemCreated,
  resetOnSubmit,
  submitLabel = 'Add Item',
  isOnboarding = false,
}: QuickAddItemFormProps) {
  const navigate = useNavigate();
  const createItem = useCreateItem();
  const { user } = useAuth();
  const { maxItems, isPaid } = useSubscription();

  const { data: itemCount = 0 } = useQuery({
    queryKey: ['my-items-count', user?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('items')
        .select('*', { count: 'exact', head: true })
        .eq('owner_id', user!.id)
        .eq('is_frozen', false);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });

  const atItemLimit = !isPaid && maxItems > 0 && itemCount >= maxItems;

  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemCategory, setItemCategory] = useState<ItemCategory | ''>('');
  const [itemBrand, setItemBrand] = useState('');
  const [itemPhotoUrl, setItemPhotoUrl] = useState('');
  const [itemPowerSource, setItemPowerSource] = useState<PowerSource | ''>('');
  const [itemCirculationMode, setItemCirculationMode] = useState<CirculationMode>(
    'RETURN_TO_OWNER_REQUIRED',
  );
  const [itemCategoryDetails, setItemCategoryDetails] = useState<CategoryDetailsState>(
    createEmptyCategoryDetailsState(),
  );
  const [additionalDetailsOpen, setAdditionalDetailsOpen] = useState(false);
  const [nameBlurred, setNameBlurred] = useState(false);
  const [categoryBlurred, setCategoryBlurred] = useState(false);
  const [photoTab, setPhotoTab] = useState<'upload' | 'library'>('upload');
  const userHasSetPhoto = useRef(false);

  const { allItems: libraryItems } = usePhotoLibrary(itemCategory || undefined);

  // When category changes away from a tool category, reset photo state so auto-suggest can fire again
  useEffect(() => {
    if (!isToolCategory(itemCategory)) {
      userHasSetPhoto.current = false;
      setPhotoTab('upload');
      setItemPhotoUrl('');
    }
  }, [itemCategory]);

  // Auto-suggest a library photo when the user types a tool name
  useEffect(() => {
    if (userHasSetPhoto.current) return;
    if (!itemCategory || !isToolCategory(itemCategory)) return;
    if (itemName.length < 3) return;

    const timer = setTimeout(() => {
      const match = findBestLibraryMatch(itemName, libraryItems);
      if (match?.url) {
        setItemPhotoUrl(match.url);
        setPhotoTab('library');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [itemName, itemCategory, libraryItems]);

  const resetForm = () => {
    setItemName('');
    setItemDescription('');
    setItemCategory('');
    setItemBrand('');
    setItemPhotoUrl('');
    setItemPowerSource('');
    setItemCirculationMode('RETURN_TO_OWNER_REQUIRED');
    setItemCategoryDetails(createEmptyCategoryDetailsState());
    setAdditionalDetailsOpen(false);
    setNameBlurred(false);
    setCategoryBlurred(false);
    setPhotoTab('upload');
    userHasSetPhoto.current = false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemName.trim() || !itemCategory) return;

    if (!isOnboarding) {
      const validationError = validateCategoryDetails(itemCategory, itemCategoryDetails);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    try {
      const createdItem = await createItem.mutateAsync({
        groupId,
        name: itemName.trim(),
        description: itemDescription.trim() || undefined,
        category: itemCategory,
        brand: isToolCategory(itemCategory) ? itemBrand.trim() || undefined : undefined,
        photoUrl: itemPhotoUrl || undefined,
        powerSource: isToolCategory(itemCategory) ? itemPowerSource || undefined : undefined,
        circulationMode: itemCirculationMode,
      });

      if (!isOnboarding) {
        await saveCategoryDetails(createdItem.id, itemCategory, itemCategoryDetails);
      }
      toast.success('Item added!');
      onItemCreated?.({ id: createdItem.id, name: createdItem.name, category: itemCategory });
      if (resetOnSubmit) {
        resetForm();
      }
    } catch {
      // handled by useCreateItem onError
    }
  };

  const updateCategoryDetail = <K extends keyof CategoryDetailsState>(
    key: K,
    patch: Partial<CategoryDetailsState[K]>,
  ) => {
    setItemCategoryDetails((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const isSubmitDisabled = !itemName.trim() || !itemCategory || createItem.isPending || atItemLimit;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Quick-Add section: always visible */}
      <div className="space-y-2">
        <Label htmlFor="quick-itemName">Name</Label>
        <Input
          id="quick-itemName"
          placeholder="e.g. Circular Saw, Catan, Gazebo"
          value={itemName}
          onChange={(e) => setItemName(e.target.value)}
          onBlur={() => setNameBlurred(true)}
          maxLength={200}
        />
        {nameBlurred && !itemName.trim() && (
          <p style={{ fontSize: 12, color: T.warning, marginTop: 2 }}>Item name is required.</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="quick-itemCategory">Category</Label>
        <Select
          value={itemCategory}
          onValueChange={(category) => {
            const nextCategory = category as ItemCategory;
            setItemCategory(nextCategory);
            setCategoryBlurred(true);
            if (!isToolCategory(nextCategory)) {
              setItemBrand('');
              setItemPowerSource('');
            }
          }}
          onOpenChange={(open) => {
            if (!open) setCategoryBlurred(true);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {ITEM_CATEGORY_OPTIONS.map((category) => (
              <SelectItem key={category.value} value={category.value}>
                {category.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {categoryBlurred && !itemCategory && (
          <p style={{ fontSize: 12, color: T.warning, marginTop: 2 }}>Please select a category.</p>
        )}
      </div>
      {!isOnboarding && (
        <div className="space-y-2">
          <Label>Photo (optional)</Label>
          {isToolCategory(itemCategory) && libraryItems.length > 0 ? (
            <>
              <PhotoSourceTabs activeTab={photoTab} onTabChange={setPhotoTab} />
              {photoTab === 'upload' ? (
                <ImageUpload
                  value={itemPhotoUrl && !isLibraryUrl(itemPhotoUrl) ? itemPhotoUrl : null}
                  onChange={(url) => {
                    setItemPhotoUrl(url || '');
                    if (url) userHasSetPhoto.current = true;
                    else userHasSetPhoto.current = false;
                  }}
                />
              ) : (
                <PhotoLibraryPicker
                  category={itemCategory}
                  onSelect={(url) => {
                    setItemPhotoUrl(url);
                    userHasSetPhoto.current = true;
                  }}
                  selectedUrl={isLibraryUrl(itemPhotoUrl) ? itemPhotoUrl : undefined}
                />
              )}
            </>
          ) : (
            <ImageUpload value={itemPhotoUrl || null} onChange={(url) => setItemPhotoUrl(url || '')} />
          )}
        </div>
      )}
      {!isOnboarding && (
        <div className="space-y-2">
          <Label htmlFor="quick-itemCirculationMode">Circulation Rules</Label>
          <Select
            value={itemCirculationMode}
            onValueChange={(val) => setItemCirculationMode(val as CirculationMode)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select circulation mode" />
            </SelectTrigger>
            <SelectContent>
              {CIRCULATION_MODES.map((cm) => (
                <SelectItem key={cm.value} value={cm.value}>
                  <span>{cm.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
            {CIRCULATION_MODES.find((cm) => cm.value === itemCirculationMode)?.description}
          </p>
        </div>
      )}
      {isOnboarding && (
        <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5, margin: 0 }}>
          You can add photos and details later from your item list.
        </p>
      )}

      {/* Additional Details toggle — hidden in onboarding mode */}
      {!isOnboarding && <button
        type="button"
        onClick={() => setAdditionalDetailsOpen((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '12px 16px',
          background: T.surfaceSubtle,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.sm,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Additional details</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            color: T.textMuted,
            transform: additionalDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
            flexShrink: 0,
          }}
        >
          <path d="M 4 6 L 8 10 L 12 6" />
        </svg>
      </button>}

      {/* Additional Details section */}
      {!isOnboarding && additionalDetailsOpen && (
        <div className="space-y-4">
          {/* Description — all categories */}
          <div className="space-y-2">
            <Label htmlFor="quick-itemDescription">Description (optional)</Label>
            <Input
              id="quick-itemDescription"
              placeholder="Any details about the item"
              value={itemDescription}
              onChange={(e) => setItemDescription(e.target.value)}
              maxLength={2000}
            />
          </div>

          {/* Tool-specific fields */}
          {isToolCategory(itemCategory) && (
            <>
              <div className="space-y-2">
                <Label htmlFor="quick-itemBrand">Brand (optional)</Label>
                <Input
                  id="quick-itemBrand"
                  placeholder="e.g. DeWalt, Bosch, Makita"
                  value={itemBrand}
                  onChange={(e) => setItemBrand(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quick-itemPowerSource">Power Source (optional)</Label>
                <Select
                  value={itemPowerSource}
                  onValueChange={(val) => setItemPowerSource(val as PowerSource)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select power source" />
                  </SelectTrigger>
                  <SelectContent>
                    {POWER_SOURCES.map((ps) => (
                      <SelectItem key={ps.value} value={ps.value}>
                        {ps.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Board Game fields */}
          {itemCategory === 'BOARD_GAME' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="quick-minPlayers">Min Players (optional)</Label>
                  <Input
                    id="quick-minPlayers"
                    type="number"
                    min={1}
                    placeholder="e.g. 2"
                    value={itemCategoryDetails.boardGame.minPlayers}
                    onChange={(e) =>
                      updateCategoryDetail('boardGame', { minPlayers: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quick-maxPlayers">Max Players (optional)</Label>
                  <Input
                    id="quick-maxPlayers"
                    type="number"
                    min={1}
                    placeholder="e.g. 4"
                    value={itemCategoryDetails.boardGame.maxPlayers}
                    onChange={(e) =>
                      updateCategoryDetail('boardGame', { maxPlayers: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quick-bgAge">Recommended Age (optional)</Label>
                <Input
                  id="quick-bgAge"
                  type="number"
                  min={0}
                  placeholder="e.g. 8"
                  value={itemCategoryDetails.boardGame.recommendedAge}
                  onChange={(e) =>
                    updateCategoryDetail('boardGame', { recommendedAge: e.target.value })
                  }
                />
              </div>
            </>
          )}

          {/* Book fields */}
          {itemCategory === 'BOOK' && (
            <div className="space-y-2">
              <Label htmlFor="quick-bookAge">Recommended Age (optional)</Label>
              <Input
                id="quick-bookAge"
                type="number"
                min={0}
                placeholder="e.g. 8"
                value={itemCategoryDetails.book.recommendedAge}
                onChange={(e) => updateCategoryDetail('book', { recommendedAge: e.target.value })}
              />
            </div>
          )}

          {/* Puzzle fields */}
          {itemCategory === 'PUZZLE' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="quick-pieceCount">Piece Count (optional)</Label>
                <Input
                  id="quick-pieceCount"
                  type="number"
                  min={1}
                  placeholder="e.g. 1000"
                  value={itemCategoryDetails.puzzle.pieceCount}
                  onChange={(e) => updateCategoryDetail('puzzle', { pieceCount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quick-puzzleAge">Recommended Age (optional)</Label>
                <Input
                  id="quick-puzzleAge"
                  type="number"
                  min={0}
                  placeholder="e.g. 8"
                  value={itemCategoryDetails.puzzle.recommendedAge}
                  onChange={(e) =>
                    updateCategoryDetail('puzzle', { recommendedAge: e.target.value })
                  }
                />
              </div>
            </div>
          )}

          {/* Toy fields */}
          {itemCategory === 'TOY' && (
            <div className="space-y-2">
              <Label htmlFor="quick-toyAge">Recommended Age (optional)</Label>
              <Input
                id="quick-toyAge"
                type="number"
                min={0}
                placeholder="e.g. 3"
                value={itemCategoryDetails.toy.recommendedAge}
                onChange={(e) => updateCategoryDetail('toy', { recommendedAge: e.target.value })}
              />
            </div>
          )}
        </div>
      )}

      {atItemLimit && (
        <p style={{ fontSize: 13, color: T.warning, padding: '4px 0', lineHeight: 1.5 }}>
          {itemCount > maxItems
            ? <>Your free plan allows {maxItems} items, but you have {itemCount}. Your items are safe. To add new ones, <span onClick={() => navigate('/account')} style={{ color: T.accent, textDecoration: 'underline', cursor: 'pointer' }}>upgrade your plan</span> or remove some items.</>
            : <>You've used {itemCount} of {maxItems} items. <span onClick={() => navigate('/account')} style={{ color: T.accent, textDecoration: 'underline', cursor: 'pointer' }}>Upgrade your plan</span> to add more.</>
          }
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitDisabled}
        style={{
          width: '100%',
          padding: '13px',
          borderRadius: RADIUS.md,
          background: isSubmitDisabled ? T.surfaceSubtle : T.accent,
          color: isSubmitDisabled ? T.textMuted : T.white,
          border: isSubmitDisabled ? `1px solid ${T.border}` : 'none',
          fontSize: 15,
          fontWeight: 600,
          cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
          boxShadow: isSubmitDisabled ? 'none' : SHADOW.btn.accent,
        }}
      >
        {createItem.isPending ? 'Adding...' : submitLabel}
      </button>
    </form>
  );
}
